import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Layout, Form, Input, Button, Flex, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, KeyOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../../store/useStore';
import { RuleObject } from 'antd/es/form';
import { StoreValue } from 'antd/es/form/interface';
import useCountdown from '../../../hooks/useCountDown';
import { resolveRequestRegion, saveUserRegion } from '../../../utils/region';
import { normalizeIdentity } from '../../../utils/identity';
import { useTranslation } from '../../../i18n';
import { HomeLink } from '../../../components/HomeLink';

type ValueType = {
  email: string;
  userName: string;
  verifyCode: string;
  password: string;
  password2: string;
};

export const Register = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { loginStore } = useStore();
  const { countdown, isRunning, startCountdown, resetCountdown } = useCountdown();

  async function onSubmit(values: ValueType) {
    if (values.password !== values.password2) {
      message.error(t('register.password_mismatch'));
    } else {
      const identity = normalizeIdentity(values.email);
      const displayName = values.userName.trim();
      const password = values.password;
      const bytes = new TextEncoder().encode(password).length;
      if (displayName.length > 12) {
        message.error(t('login.display_name_max'));
        return;
      }
      if (!/^[0-9]{6}$/.test(values.verifyCode)) {
        message.error(t('login.code_digits'));
        return;
      }
      if (password.length < 12 || password.length > 72 || bytes > 72) {
        message.error(t('login.password_rule'));
        return;
      }
      const res = await loginStore.register({
        identity,
        code: values.verifyCode,
        password,
        displayName
      });
      if (res.code === 200) {
        saveUserRegion(resolveRequestRegion());
        navigate('/creator/login');
      } else {
        message.error(res.message || t('login.register_failed'));
      }
    }
  }

  useEffect(() => {
    if (!isRunning) resetCountdown();
  });

  const validateEmail = (
    rule: RuleObject,
    value: StoreValue,
    callback: (error?: string) => void
  ) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (value && emailRegex.test(value)) {
      callback();
    } else {
      callback(t('register.email_invalid'));
    }
  };

  const sendCode = async () => {
    const email = normalizeIdentity(String(form.getFieldValue('email') ?? ''));
    if (!email.includes('@') || isRunning) {
      message.error(t('register.email_incorrect'));
      return;
    }
    const res = await loginStore.sendVerificationCode(email, 'REGISTER');
    if (res.code === 200) {
      startCountdown();
    } else {
      message.error(res.message || t('login.send_failed'));
    }
  };

  return (
    <Layout className='login-layout'>
      <HomeLink className='auth-home-link' />
      <div className='login'>
        <Flex align='center' style={{ paddingBottom: '2rem' }}>
          <img
            src={require('../../../assets/images/logo.png')}
            alt=''
            style={{ width: '44px', height: '44px' }}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('register.title')}
          </Typography.Title>
        </Flex>
        <Form<ValueType> name='register' form={form} style={{ width: '100%' }} onFinish={onSubmit}>
          <Form.Item
            name='email'
            rules={[
              {
                required: true,
                validator: validateEmail,
              },
            ]}
          >
            <Input
              prefix={<MailOutlined className='site-form-item-icon' />}
              placeholder={t('register.email_placeholder')}
              size='large'
            />
          </Form.Item>
          <Flex>
            <Form.Item
              className='verifycode-item'
              name='verifyCode'
              rules={[
                {
                  required: true,
                  message: t('login.code_required'),
                },
              ]}
            >
              <Input
                prefix={<KeyOutlined className='site-form-item-icon' />}
                placeholder={t('login.code_required')}
                size='large'
              />
            </Form.Item>
            <Button size='large' style={{ marginLeft: '8px', fontSize: '13px' }} onClick={sendCode}>
              {isRunning ? `${countdown} s` : t('register.send_code')}
            </Button>
          </Flex>
          <Form.Item name='userName'>
            <Input
              prefix={<UserOutlined className='site-form-item-icon' />}
              placeholder={t('login.username_placeholder')}
              size='large'
            />
          </Form.Item>
          <Form.Item name='password'>
            <Input.Password
              prefix={<LockOutlined className='site-form-item-icon' />}
              placeholder={t('login.password_placeholder')}
              size='large'
            />
          </Form.Item>
          <Form.Item name='password2'>
            <Input.Password
              prefix={<LockOutlined className='site-form-item-icon' />}
              placeholder={t('register.password_again')}
              size='large'
            />
          </Form.Item>
          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              className='login-form-button'
              style={{ width: '100%' }}
              size='large'
            >
              {t('login.register_btn')}
            </Button>
          </Form.Item>
          <Link
            style={{
              width: '100%',
              display: 'block',
              textAlign: 'center',
            }}
            to='/creator/login'
          >
            {t('login.go_login')}
          </Link>
        </Form>
      </div>
    </Layout>
  );
});
